import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { RecordVoiceOver, ExpandMore, Work, LocalAtm } from '@mui/icons-material'
import { ContentCopy, Star } from 'mdi-material-ui'
import { inject } from 'mobx-react'
import moment from 'moment'
import React from 'react'
import { useParams } from 'react-router-dom'
import CardWithTitle from 'src/common/CardWithTitle/CardWithTitle'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import CopySuccessSnackbar from 'src/common/CopySuccessSnackbar'
import { GetCaseInboxItem, GetDefaultCaseInboxItem } from 'src/services/WorkflowService'
import { IStores } from 'src/Stores'
import { InboxItemType } from 'src/utils/InboxItemType'
import { CaseInboxItem } from 'src/viewModels/workflow/CaseInboxItem'
import { caseAppointmentStatusOptions } from 'src/viewModels/workflow/CaseAppointmentStatusOption'
import WorkflowInbox from './WorkflowInbox'
import PaymentHistoryModal from './modals/obm/PaymentHistoryModal'

interface ICaseInboxProps {
  caseId?: string
  inboxId?: string
  rerouteToPath?: (path: string) => void
  openReferralSourceContactModal?: () => void
}

class CaseInbox extends React.Component<ICaseInboxProps> {
  public state = {
    headerInfo: undefined as unknown as CaseInboxItem,
    expandCaseDetails: false,
    setOpenModal: false,
    selectedDetail: null as any,
    paymentHistoryModalOpen: false,
    selectedCaseLineItemId: null,
    copied: false,
  }

  public async componentDidMount(): Promise<void> {
    const { caseId, inboxId } = this.props
    if (caseId && inboxId) {
      await GetCaseInboxItem(caseId, inboxId).then((response: CaseInboxItem) => {
        const hasCaseDetails = response?.case?.caseDetails?.length > 0
        this.setState({ headerInfo: response, expandCaseDetails: hasCaseDetails })
      })
    } else if (caseId) {
      await GetDefaultCaseInboxItem(caseId).then((response: CaseInboxItem) => {
        const hasCaseDetails = response?.case?.caseDetails?.length > 0
        this.setState({ headerInfo: response, expandCaseDetails: hasCaseDetails })
      })
    }
  }

  public handleOpenModal = (detail: any) => {
    this.setState({ setOpenModal: true, selectedDetail: detail })
  }

  public handleCloseModal = () => {
    this.setState({ setOpenModal: false, selectedDetail: null })
  }

  public handleOpenPaymentHistoryModal = (caseLineItemId: number) => {
    this.setState({
      paymentHistoryModalOpen: true,
      selectedCaseLineItemId: caseLineItemId,
    })
  }

  public handleClosePaymentHistoryModal = () => {
    this.setState({ paymentHistoryModalOpen: false, selectedCaseLineItemId: null })
  }

  public formatBillingDetails = (
    billingAddress: string | undefined,
    billingCity: string | undefined,
    billingState: string | undefined,
    billingZip: string | undefined
  ) => {
    return [billingAddress, billingCity, billingState, billingZip]
      .filter((item) => item)
      .join(', ')
  }

  public formatPhoneNumber(phone?: string): string {
    if (!phone) {
      return '[Unknown]'
    }
    const cleaned = ('' + phone).replace(/\D/g, '')
    if (cleaned.length !== 10) {
      return phone
    }
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`
  }

  public formatCurrency(amount?: number): string {
    if (amount == null) {
      return ' '
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  public renderCase() {
    const { headerInfo } = this.state
    const caseDetails = headerInfo?.case?.caseDetails || []
    const patient = headerInfo?.case?.patient
    const getStatusName = (id: string) =>
      caseAppointmentStatusOptions.find(
        (opt: { id: string; name: string }) => String(opt.id) === String(id)
      )?.name || id

    if (!headerInfo) {
      return (
        <div
          style={{
            position: 'fixed',
            display: 'flex',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 500,
          }}
        >
          <CircularProgress size={100} />
        </div>
      )
    }

    return (
      <Grid container spacing={2}>
        {/* Top Row: One Item */}
        <Grid item xs={12} style={{ marginTop: '8px' }}>
          <CardWithTitle
            title={`Case ${headerInfo?.case?.caseNumber}`}
            icon={Work}
            subTitle={`(Status: ${headerInfo?.case?.currentStatus || ''})`}
          >
            <Grid container item direction="row" spacing={2}>
              <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                <strong>Patient Name: </strong>
                <span>
                  {patient.patientFirstName || '[Unknown]'}{' '}
                  {patient.patientLastName || '[Unknown]'}
                </span>
              </Grid>
              <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                <strong>Date of Birth: </strong>
                <span>
                  {patient.dob
                    ? moment.utc(patient.dob).format('MM/DD/YYYY')
                    : '[Unknown]'}
                </span>
              </Grid>
              <Grid item xs="auto" style={{ whiteSpace: 'nowrap' }}>
                <strong>Gender: </strong>
                <span>
                  {patient.gender
                    ? patient.gender === 'F'
                      ? 'Female'
                      : patient.gender === 'M'
                        ? 'Male'
                        : patient.gender
                    : '[Unknown]'}
                </span>
              </Grid>
              <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                <strong>Cell Phone Number: </strong>
                <span>{this.formatPhoneNumber(patient.patientPhoneC)}</span>
              </Grid>
              <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                <strong>Home Phone Number: </strong>
                <span>{this.formatPhoneNumber(patient.patientPhoneH)}</span>
              </Grid>
              <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                <strong>Work Phone Number: </strong>
                <span>{this.formatPhoneNumber(patient.patientPhoneW)}</span>
              </Grid>
              <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                <strong>Patient Address: </strong>
                <span>
                  {this.formatBillingDetails(
                    patient.patientAddress,
                    patient.patientCity,
                    patient.patientState,
                    patient.patientZip
                  )?.trim()
                    ? this.formatBillingDetails(
                        patient.patientAddress,
                        patient.patientCity,
                        patient.patientState,
                        patient.patientZip
                      )
                    : '[Unknown]'}
                </span>
              </Grid>
              <Grid
                item
                sm={12}
                style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}
              >
                <strong>Case Notes: </strong>
                <br />
                <TextField
                  name="notes"
                  variant="outlined"
                  value={patient.notes}
                  fullWidth
                  multiline
                  disabled
                  minRows={4}
                />
              </Grid>
            </Grid>
          </CardWithTitle>
        </Grid>

        {/* Wrap the ExpansionPanel in a Grid container */}
        <Grid
          item
          xs={12}
          style={{ marginTop: '8px', marginRight: '5px', marginLeft: '5px' }}
        >
          <Accordion
            expanded={this.state.expandCaseDetails}
            onChange={() =>
              this.setState({ expandCaseDetails: !this.state.expandCaseDetails })
            }
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Grid
                item
                xs={12}
                container
                justifyContent="space-between"
                alignItems="center"
                style={{ paddingTop: '5px' }}
              >
                <Typography display="inline" color="textSecondary" variant="body1">
                  CASE DETAILS
                </Typography>
                <Typography
                  display="inline"
                  color="textPrimary"
                  variant="subtitle1"
                  style={{ textAlign: 'right', flex: 1 }}
                >
                  <div style={{ float: 'right' }}>
                    <strong>Total Invoiced: </strong>
                    <span style={{ marginRight: '5px' }}>
                      {headerInfo?.case?.totalInvoiced
                        ? this.formatCurrency(headerInfo?.case?.totalInvoiced)
                        : '[N/A]'}
                    </span>
                    <strong>Balance Due: </strong>
                    <span style={{ marginRight: '5px' }}>
                      {headerInfo?.case?.balanceDue !== null &&
                      headerInfo?.case?.balanceDue !== undefined
                        ? this.formatCurrency(headerInfo?.case?.balanceDue)
                        : '[N/A]'}
                    </span>
                    <span>{`(${caseDetails.length} items)`}</span>
                  </div>
                </Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails style={{ padding: '16px' }}>
              <Grid container spacing={2}>
                {caseDetails.map((detail, index) => (
                  <Grid key={index} item xs={12} sm={6} md={6} lg={4}>
                    <Paper
                      style={{
                        position: 'relative',
                        padding: '9px',
                        borderRadius: '4px',
                        minHeight: '588px',
                      }}
                      elevation={4}
                    >
                      {detail.paidInFull && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '14px',
                            left: '0',
                            padding: '6px 18px',
                            background: '#49cc90',
                            color: 'inherit',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            borderTopRightRadius: '20px',
                            borderBottomRightRadius: '20px',
                            boxShadow: '0 2px 8px rgba(76,175,80,0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            pointerEvents: 'none',
                          }}
                          aria-label="Paid in full"
                        >
                          <span style={{ marginRight: '8px', fontSize: '18px' }}>✓</span>
                          PAID
                        </div>
                      )}
                      <div>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Group #:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${detail.groupNumber}`}
                                {detail.id === headerInfo?.case?.defaultLineItemId && (
                                  <>
                                    &nbsp;&nbsp;
                                    <Tooltip title="Default Line Item">
                                      <Star
                                        fontSize="small"
                                        color="secondary"
                                        style={{
                                          verticalAlign: 'middle',
                                        }}
                                      />
                                    </Tooltip>
                                  </>
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Type:</Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography variant="body2" color="textPrimary">
                                {detail.typeCode || detail.typeCodeDescription
                                  ? `${detail.typeCode || ''}${
                                      detail.typeCode && detail.typeCodeDescription
                                        ? ': '
                                        : ''
                                    }${detail.typeCodeDescription || ''}`
                                  : ''}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Referral Source:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="textPrimary"
                                style={{ cursor: 'pointer' }}
                                onClick={() => this.handleOpenModal(detail)}
                              >
                                {detail.referralSource?.sourceName || ''}
                                {detail.referralSource?.sourceName && (
                                  <>
                                    &nbsp;&nbsp;
                                    <Tooltip title="Show Referral Source Contact">
                                      <RecordVoiceOver
                                        fontSize="small"
                                        color="secondary"
                                        style={{
                                          verticalAlign: 'middle',
                                        }}
                                      />
                                    </Tooltip>
                                  </>
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Referral Contact:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.referralSourceContact?.rsContactFirstName || ''
                                } ${
                                  detail.referralSourceContact?.rsContactLastName || ''
                                }`.trim()}
                              </Typography>
                            </Grid>
                            <Grid container spacing={1}>
                              <Grid
                                item
                                xs={6}
                                style={{ textAlign: 'right', fontWeight: 'bold' }}
                              >
                                <Typography variant="body2">Medical Provider:</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="textPrimary">
                                  {detail.providerName || ''}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Date For Rush:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.dateForRush
                                    ? moment.utc(detail.dateForRush).format('MM/DD/YYYY')
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2"># of Hours:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {detail.numberOfHours ? detail.numberOfHours : ''}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{
                                textAlign: 'right',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Typography variant="body2">Price Per Hour:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  this.formatCurrency(detail.billingPricePerHour) || ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Invoice #:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${detail.invoiceNumber || ''}`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Invoice Amount:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.invoiceAmount != null
                                    ? this.formatCurrency(detail.invoiceAmount)
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Amount Paid:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                <span
                                  style={{ display: 'inline-flex', alignItems: 'center' }}
                                >
                                  {this.formatCurrency(detail.amountPaid)}
                                  &nbsp;&nbsp;
                                  {(detail.amountPaid ?? 0) > 0 && (
                                    <Tooltip title="Show Received Checks">
                                      <LocalAtm
                                        fontSize="small"
                                        color="secondary"
                                        style={{
                                          verticalAlign: 'middle',
                                          cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                          this.handleOpenPaymentHistoryModal(detail.id)
                                        }
                                      />
                                    </Tooltip>
                                  )}
                                </span>
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Invoice Date:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.invoiceDate
                                    ? moment.utc(detail.invoiceDate).format('MM/DD/YYYY')
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Invoice Notes:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {detail.invoiceSpecificText}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Date of Service:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.dos
                                    ? moment.utc(detail.dos).format('MM/DD/YYYY')
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Status:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {getStatusName(detail.apptStatus ?? '')}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">
                                Date Records Received:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.dateRecordsReceived
                                    ? moment
                                        .utc(detail.dateRecordsReceived)
                                        .format('MM/DD/YYYY')
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">
                                Date of Chrono Complete:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.dateChronoComplete
                                    ? moment
                                        .utc(detail.dateChronoComplete)
                                        .format('MM/DD/YYYY')
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">
                                # of Hours For Chrono:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${detail.numberOfHoursForChrono || ' '}`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Chrono By:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${detail.chronoBy || ' '}`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">
                                Date Records Reviewed:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.dateRecordsReviewed
                                    ? moment
                                        .utc(detail.dateRecordsReviewed)
                                        .format('MM/DD/YYYY')
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Date Dictated:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {detail.dateDictated
                                  ? moment.utc(detail.dateDictated).format('MM/DD/YYYY')
                                  : ''}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">
                                Date Back From Transcription:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {detail.dateBackFromTranscription
                                  ? moment
                                      .utc(detail.dateBackFromTranscription)
                                      .format('MM/DD/YYYY')
                                  : ''}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Date Finalized:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.dateFinalized
                                    ? moment
                                        .utc(detail.dateFinalized)
                                        .format('MM/DD/YYYY')
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid
                              item
                              xs={6}
                              style={{ textAlign: 'right', fontWeight: 'bold' }}
                            >
                              <Typography variant="body2">Date Report Sent:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textPrimary">
                                {`${
                                  detail.dateReportSent
                                    ? moment
                                        .utc(detail.dateReportSent)
                                        .format('MM/DD/YYYY')
                                    : ''
                                }`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </div>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    )
  }

  public render() {
    const { setOpenModal, selectedDetail } = this.state
    return (
      <>
        <WorkflowInbox
          inboxType={InboxItemType.Case}
          item={this.renderCase()}
          headerInfo={this.state.headerInfo}
          icon={<Work />}
        />
        <PaymentHistoryModal
          open={this.state.paymentHistoryModalOpen}
          caseLineItemId={this.state.selectedCaseLineItemId}
          onClose={this.handleClosePaymentHistoryModal}
        />
        <Dialog
          maxWidth="xl"
          fullWidth
          open={setOpenModal}
          onClose={this.handleCloseModal}
        >
          <ResponsiveAppBar
            title="Referral Source Contact"
            pageIcon={<RecordVoiceOver />}
            isModal={true}
          />
          <DialogContent>
            <Box padding={3}>
              {selectedDetail ? (
                <>
                  <Box marginBottom={3}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      REFERRAL DETAILS
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body1">
                          <strong>Referral Source:</strong>{' '}
                          {selectedDetail.referralSourceContact?.referralSource
                            ?.sourceName ||
                            selectedDetail.referralSource?.sourceName ||
                            '[Unknown]'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body1">
                          <strong>Contact Name:</strong>{' '}
                          {selectedDetail.referralSourceContact?.rsContactFirstName ||
                          selectedDetail.referralSourceContact?.rsContactLastName
                            ? [
                                selectedDetail.referralSourceContact?.rsContactLastName,
                                selectedDetail.referralSourceContact?.rsContactFirstName,
                              ]
                                .filter(Boolean)
                                .join(', ')
                            : '[Unknown]'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body1">
                          <strong>Contact Type:</strong>{' '}
                          {selectedDetail.referralSourceContact?.rsContactType ||
                            '[Unknown]'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body1">
                          <strong>Referral Speciality:</strong>{' '}
                          {selectedDetail.referralSourceContact?.referralSource
                            ?.speciality || '[Unknown]'}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body1">
                          <strong>Billing Address:</strong>{' '}
                          {this.formatBillingDetails(
                            selectedDetail.referralSourceContact?.referralSource
                              ?.billingAddress,
                            selectedDetail.referralSourceContact?.referralSource
                              ?.billingCity,
                            selectedDetail.referralSourceContact?.referralSource
                              ?.billingState,
                            selectedDetail.referralSourceContact?.referralSource
                              ?.billingZip || '[Unknown]'
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body1">
                          <strong>Billing Phone #:</strong>{' '}
                          {selectedDetail.referralSourceContact?.referralSource
                            ?.billingPhone || '[Unknown]'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box marginBottom={3}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      CONTACT DETAILS
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body1">
                          <strong>Contact Phone #:</strong>{' '}
                          {selectedDetail.referralSourceContact?.rsContactPhone ||
                            '[Unknown]'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body1">
                          <strong>Contact Fax #:</strong>{' '}
                          {selectedDetail.referralSourceContact?.rsContactFax ||
                            '[Unknown]'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="body1"
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          <strong>Contact Email:</strong>&nbsp;
                          {selectedDetail.referralSourceContact?.rsContactEmail ||
                            '[Unknown]'}
                          {selectedDetail.referralSourceContact?.rsContactEmail && (
                            <>
                              &nbsp;&nbsp;
                              <Tooltip title="Copy Email">
                                <ContentCopy
                                  fontSize="small"
                                  color="secondary"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      selectedDetail.referralSourceContact.rsContactEmail
                                    )
                                    this.setState({ copied: true })
                                    setTimeout(
                                      () => this.setState({ copied: false }),
                                      2000
                                    )
                                  }}
                                />
                              </Tooltip>
                            </>
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    COMMENTS
                  </Typography>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      variant="outlined"
                      value={
                        selectedDetail.referralSourceContact?.rsContactComments ||
                        'No comments available.'
                      }
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                </>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseModal} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <CopySuccessSnackbar
          open={this.state.copied}
          onClose={() => this.setState({ copied: false })}
          message="Email copied to clipboard!"
        />
      </>
    )
  }
}

const InjectedCaseInbox = inject<IStores, ICaseInboxProps, Partial<ICaseInboxProps>, any>(
  () => ({})
)(CaseInbox)

const CaseInboxWrapper: React.FC = () => {
  const params = useParams<{ caseId?: string; inboxId?: string }>()
  return <InjectedCaseInbox caseId={params.caseId} inboxId={params.inboxId} />
}

export default CaseInboxWrapper
